/**
 * useSNConfig() — konfigurasi Serial Number (SN) dari Global Information (GET /transaksi/sn/config).
 *
 * Padanan MGMX Frontend/src/hooks/useSNConfig.ts (Delphi Units/MGConstant.pas):
 *  - SettingGaransiToko       → `on`         (saklar; mati → barang "Stok SN" diperlakukan barang biasa.
 *                                             Ban (Reserved_int1 = 5) khusus MGTRUCK, selalu butuh SN)
 *  - SettingPeringatanSN      → `label`      (bawaan 'Serial Number'; dipakai di semua judul & pesan)
 *  - SettingPeringatanSNShort → `labelShort` (bawaan 'SN'; dipakai di judul kolom/tombol)
 *  - SettingInputFormatSN     → `inputFormat` (0 = teks, 1 = tanggal dd/mm/yyyy)
 *  - SettingMultipleSN        → `multipleSN` selalu 0 (1 nomor = 1 unit, tidak dibuka di web)
 *
 * Bedanya dengan MGMX: konfigurasi diambil dari endpoint SN sendiri (MGTRUCK belum punya GET /setting umum) dan di-cache
 * per sesi. Selama `loading`, `on` memakai bawaan MGTRUCK (hidup) supaya form tidak berkedip.
 * Setelah Global Information disimpan panggil `refreshSNConfig()` agar label/saklar langsung berlaku tanpa login ulang.
 */
'use client';

import { useEffect, useState } from 'react';
import api from '@/lib/axios';
import { terapkanLabelSNMenu } from '@/lib/menuTree';

export const DEFAULT_SN_LABEL = 'Serial Number';
export const DEFAULT_SN_LABEL_SHORT = 'SN';
/** Panjang kolom SN (varchar(50) di mginmbrgdsn & tabel *dsn) */
export const SN_MAX_LENGTH = 50;
/** Maks panjang label SN di Global Information (sama BE SN_LABEL_MAX) */
export const SN_LABEL_MAX = 30;

export interface SNConfig {
    /** SettingGaransiToko = 1 */
    on: boolean;
    /** SettingPeringatanSN (bawaan 'Serial Number') */
    label: string;
    /** SettingPeringatanSNShort (bawaan 'SN') */
    labelShort: string;
    /** SettingInputFormatSN: 0 = teks (bawaan), 1 = tanggal dd/mm/yyyy */
    inputFormat: 0 | 1;
    /** SettingMultipleSN — selalu 0 */
    multipleSN: 0 | 1;
    /** Konfigurasi masih dimuat */
    loading: boolean;
}

const BAWAAN: SNConfig = {
    on: true,
    label: DEFAULT_SN_LABEL,
    labelShort: DEFAULT_SN_LABEL_SHORT,
    inputFormat: 0,
    multipleSN: 0,
    loading: true,
};

type Raw = Partial<Record<'on' | 'label' | 'labelShort' | 'inputFormat' | 'multipleSN', unknown>>;

/** Delphi SettingPeringatanSN: tanda petik tunggal → spasi, Trim, kosong → nilai bawaan. */
const labelDari = (value: unknown, bawaan: string): string => {
    const s = String(value ?? '').replace(/'/g, ' ').trim();
    return s || bawaan;
};

/** Pure resolver: respons BE → SNConfig (bisa dipakai di luar React). */
export function buildSNConfig(raw: Raw | null | undefined, loading = false): SNConfig {
    if (!raw) return { ...BAWAAN, loading };
    return {
        on: raw.on === undefined ? BAWAAN.on : raw.on === true || Number(raw.on) === 1,
        label: labelDari(raw.label, DEFAULT_SN_LABEL),
        labelShort: labelDari(raw.labelShort, DEFAULT_SN_LABEL_SHORT),
        inputFormat: Number(raw.inputFormat) === 1 ? 1 : 0,
        multipleSN: 0,
        loading,
    };
}

let cache: SNConfig | null = null;
let inflight: Promise<SNConfig> | null = null;
const listeners = new Set<(cfg: SNConfig) => void>();

async function fetchSNConfig(force = false): Promise<SNConfig> {
    if (cache && !force) return cache;
    if (inflight) return inflight;
    inflight = api
        .get('/transaksi/sn/config')
        .then((res) => {
            cache = buildSNConfig(res.data?.data, false);
            // Judul menu SN (sidebar, breadcrumb) mengikuti label — diterapkan SEBELUM komponen diberi tahu
            terapkanLabelSNMenu(cache.label, cache.labelShort);
            listeners.forEach((fn) => fn(cache as SNConfig));
            return cache;
        })
        .catch((err) => {
            console.error('useSNConfig load failed:', err);
            // Gagal muat → pakai bawaan MGTRUCK (jangan menyembunyikan SN karena error jaringan), tanpa di-cache
            return buildSNConfig(null, false);
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

/** Muat ulang konfigurasi (panggil setelah Global Information disimpan). */
export const refreshSNConfig = (): Promise<SNConfig> => fetchSNConfig(true);

export default function useSNConfig(): SNConfig {
    const [cfg, setCfg] = useState<SNConfig>(() => cache ?? BAWAAN);

    useEffect(() => {
        let mounted = true;
        const onChange = (next: SNConfig) => { if (mounted) setCfg(next); };
        listeners.add(onChange);
        // Bila cache sudah ada, state awal (useState) sudah memakainya; perubahan berikutnya datang lewat listener
        if (!cache) fetchSNConfig().then((next) => { if (mounted) setCfg(next); });
        return () => {
            mounted = false;
            listeners.delete(onChange);
        };
    }, []);

    return cfg;
}

// ─── Helper teks SN (bersama) ────────────────────────────────────────────────

/**
 * Normalisasi SN = Delphi: Trim + UpperCase (UpperCase Delphi hanya huruf a-z ASCII) — sama BE `sn.service.normalizeSN`.
 */
export function normalizeSN(value: unknown): string {
    return String(value ?? '').trim().replace(/[a-z]+/g, (m) => m.toUpperCase());
}

/**
 * Delphi TMGFungsi.CekFormatSN (MGFungsi.pas:247-274). inputFormat 0 → selalu valid.
 * 1 → wajib dd/mm/yyyy dan tanggal valid (sama BE `sn.service.cekFormatSN`).
 */
export function cekFormatSN(sn: string, inputFormat: number): boolean {
    if (Number(inputFormat) !== 1) return true;
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(sn);
    if (!m) return false;
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    if (yyyy < 1 || mm < 1 || mm > 12 || dd < 1) return false;
    const kabisat = (yyyy % 4 === 0 && yyyy % 100 !== 0) || yyyy % 400 === 0;
    const hariBulan = [31, kabisat ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mm - 1];
    return dd <= hariBulan;
}

/** 'Format ' + PeringatanSN + ' Salah (dd/mm/yyyy)' (MGINMBrgDSNInputForm.pas:510) */
export const pesanFormatSNSalah = (label: string): string => `Format ${label} Salah (dd/mm/yyyy)`;

/**
 * Angka qty di teks SN = Delphi FloatToStr locale Indonesia (tanpa pemisah ribuan, desimal koma): 3 → "3", 2.5 → "2,5".
 * Sama dengan BE (pesan FE & BE harus identik).
 */
export function formatQtySN(value: unknown): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    const r = Math.round(n * 1e6) / 1e6;
    return String(r === 0 ? 0 : r).replace('.', ',');
}

/** Pesan SN (teks Delphi, label dari SettingPeringatanSN) — subset yang dipakai dialog SN masuk. */
export const pesanSN = {
    /** Format(wmFieldIsNull, [PeringatanSN]) — MGMessage.pas:41 */
    wajibIsi: (label: string) => `Field ${label} harus diisi.`,
    /** 'Barang ' + KdMBrg + ' memiliki ' + PeringatanSN + ' yang kembar (' + SN + ')' (MGINTSABrgInputForm.pas:859) */
    kembar: (kdMBrg: string, label: string, sn: string) => `Barang ${kdMBrg} memiliki ${label} yang kembar (${sn})`,
    /** CekJmlSN mode 1 — Saldo Awal (MGINTSABrgInputForm.pas:849) */
    melebihi: (kdMBrg: string, label: string) => `Jumlah ${label} untuk barang ${kdMBrg} Melebihi Jumlah Quantity`,
    /** 'Hanya ' + FloatToStr(QtySN) + ' saja perlu diisi ' + PeringatanSN + 'nya' (MGINMBrgDSNInputForm.pas:589) */
    hanyaPerlu: (qty: unknown, label: string) => `Hanya ${formatQtySN(qty)} saja perlu diisi ${label}nya`,
    /** Format(cmRecDelete, [PeringatanSN]) — MGMessage.pas:73 */
    hapus: (label: string) => `Apakah anda yakin data ${label} akan dihapus ?`,
    /** Web (bukan Delphi): Batal dialog SN saat isian berubah */
    batalkanPerubahan: (label: string) => `Batalkan perubahan ${label}?`,
};
